import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { colors } from "theme";

import BottomDrawer from "components/BottomDrawer";
import Button from "components/Button";
import ClientTranslation from "components/ClientTranslation";
import Flex from "components/Flex";
import { Image } from "components/Image";
import PageLayout from "components/PageLayout";
import Text from "components/Text";

import { clientConfigAtom } from "atoms/clientConfig";
import { pageDrawerAtom } from "atoms/pageDrawer";
import { tosAcceptedAtom } from "atoms/tosAccepted";
import { useClientTranslation } from "hooks/useClientTranslation";
import { useEventTracking } from "hooks/useEventTracking";
import { usePageRouting } from "hooks/usePageRouting";

import { useStyles } from "./styles";

const DEFAULT_IMAGE: Image = {
  src: "/assets/svg/welcome.svg",
};

export const Welcome = () => {
  const {
    displayName: client,
    welcome: { termsLink, termsOfUse, termsOfUseModal, illustrations = {} },
  } = useAtomValue(clientConfigAtom);

  const image = {
    ...DEFAULT_IMAGE,
    ...(illustrations?.main ?? {}),
  };

  const { t } = useClientTranslation();
  const { classes, theme } = useStyles({ image });
  const { onNext } = usePageRouting();
  const { trackEvent } = useEventTracking();

  const toggleDrawer = useSetAtom(pageDrawerAtom);
  const [tosAccepted, setTosAccepted] = useAtom(tosAcceptedAtom);

  const onContinue = () => {
    trackEvent({ name: "Welcome - continue" });

    if (termsOfUseModal && !tosAccepted) {
      toggleDrawer();
    } else {
      onNext();
    }
  };

  return (
    <PageLayout showNavbar title={t("welcome.header")} contentSpacing="space-between" dataTestId="welcomeHeader">
      <div className={classes.illustration} />
      <Flex px={32} py={24} gap={16}>
        {termsOfUse && (
          <Flex gap={8}>
            <Text sizeVariant="md" weight={500} color={colors.grey[80]}>
              {t("welcome.extra_terms_of_use.header")}
            </Text>
            <Text color={colors.grey[70]}>{t("welcome.extra_terms_of_use.body")}</Text>
          </Flex>
        )}
        <Flex gap={8}>
          <Button onClick={onContinue}>{t("global.continue")}</Button>
          {termsLink && (
            <Text color={colors.grey[70]}>
              <ClientTranslation i18nKey="welcome.terms_and_conditions">
                By clicking “Continue”, you agree to {{ client }}’s
                <a href={termsLink} style={{ color: theme.other.primary[60] }} target="_blank" rel="noreferrer">
                  Terms and conditions
                </a>
              </ClientTranslation>
            </Text>
          )}
        </Flex>
      </Flex>
      {termsOfUseModal && (
        <BottomDrawer
          heading={t("welcome.terms_of_conditions.modal.header")}
          stickyBoxShadow={true}
          callToActionText={t("global.agree")}
          fullScreen={true}
          callToActionOnClick={() => {
            setTosAccepted(true);
            trackEvent({ name: "Welcome - toc" });
            onNext();
          }}
        >
          <ClientTranslation i18nKey="welcome.terms_of_conditions.modal.body">
            {t("welcome.terms_of_conditions.modal.body")}
          </ClientTranslation>
        </BottomDrawer>
      )}
    </PageLayout>
  );
};
